/**
 * Lightweight Google Places Autocomplete loader.
 *
 * Loads the Maps JS SDK once (idempotent — safe to call from multiple
 * components). Returns a Promise that resolves once `window.google.maps.places`
 * is available, then callers can attach `new google.maps.places.Autocomplete`.
 *
 * Strategy: lazy-loaded only when a form mounts, never on first paint, so
 * the Maps script doesn't bloat homepage LCP. All failures are non-fatal —
 * the plain text input always remains fully usable as a fallback.
 */

let loaderPromise = null;
let authFailed = false;
const authFailListeners = new Set();

const KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;

// Google fires this global callback when the API key is rejected
// (e.g. RefererNotAllowedMapError, InvalidKeyMapError, BillingNotEnabled).
// We mark the loader as failed so subsequent calls bail out instantly
// and notify any already-attached autocomplete instances so they can
// restore the input to its original plain-text state (Google's default
// behaviour is to mutate the placeholder with "Oops! Something went wrong.").
if (typeof window !== 'undefined') {
  window.gm_authFailure = () => {
    authFailed = true;
    console.warn(
      '[GoogleMaps] Auth failure — Places Autocomplete disabled. ' +
      'Whitelist this domain in Google Cloud Console > Credentials > HTTP referrers. ' +
      'Form inputs will continue to work as plain text fields.'
    );
    authFailListeners.forEach((fn) => { try { fn(); } catch (_) { /* noop */ } });
    authFailListeners.clear();
  };
}

export function loadGoogleMapsPlaces() {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Cannot load Google Maps in non-browser env'));
  }
  if (authFailed) {
    return Promise.reject(new Error('Google Maps auth previously failed'));
  }
  if (window.google && window.google.maps && window.google.maps.places) {
    return Promise.resolve(window.google);
  }
  if (loaderPromise) return loaderPromise;
  if (!KEY) {
    return Promise.reject(new Error('Missing REACT_APP_GOOGLE_MAPS_KEY'));
  }

  loaderPromise = new Promise((resolve, reject) => {
    const cbName = '__hgmInitMaps_' + Math.random().toString(36).slice(2, 8);
    window[cbName] = () => {
      try {
        if (window.google && window.google.maps && window.google.maps.places) {
          resolve(window.google);
        } else {
          reject(new Error('Google Maps loaded but Places library missing'));
        }
      } finally {
        try { delete window[cbName]; } catch (_) { window[cbName] = undefined; }
      }
    };
    const s = document.createElement('script');
    s.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(KEY)}&libraries=places&loading=async&callback=${cbName}`;
    s.async = true;
    s.defer = true;
    s.onerror = () => {
      // Reset the cache so future mounts can retry if the network was the
      // root cause (e.g. transient offline state).
      loaderPromise = null;
      reject(new Error('Failed to load Google Maps script'));
    };
    document.head.appendChild(s);
  });

  return loaderPromise;
}

/**
 * Attach a UK-restricted Places Autocomplete instance to a single text input
 * element. Calls `onSelect` with the structured place when the user picks a
 * suggestion. Returns a cleanup function.
 *
 * If the API fails to load or auth is rejected, this is a silent no-op —
 * the input remains a fully functional plain text field.
 */
export async function attachUkAddressAutocomplete(inputEl, onSelect) {
  if (!inputEl) return () => {};
  // Snapshot the original placeholder so we can restore it if Google mutates
  // the input on auth failure ("Oops! Something went wrong." overlay).
  const originalPlaceholder = inputEl.getAttribute('placeholder') || '';
  const restoreInput = () => {
    try {
      inputEl.setAttribute('placeholder', originalPlaceholder);
      inputEl.removeAttribute('aria-invalid');
      // Google adds these classes when binding; remove so the field looks
      // like a normal text input again.
      inputEl.classList.remove('pac-target-input');
    } catch (_) { /* noop */ }
  };
  try {
    const google = await loadGoogleMapsPlaces();
    if (authFailed) { restoreInput(); return () => {}; }
    const ac = new google.maps.places.Autocomplete(inputEl, {
      fields: ['address_components', 'geometry', 'formatted_address', 'name'],
      types: ['address'],
      componentRestrictions: { country: ['gb'] },
    });
    // If auth fails AFTER attachment (Google validates async), restore the
    // input so the user never sees Google's default error placeholder.
    authFailListeners.add(restoreInput);
    const listener = ac.addListener('place_changed', () => {
      const place = ac.getPlace();
      if (!place || !place.address_components) return;
      const get = (type, useShort = false) => {
        const c = place.address_components.find((x) => x.types.includes(type));
        if (!c) return '';
        return useShort ? c.short_name : c.long_name;
      };
      const lat = place.geometry && place.geometry.location ? place.geometry.location.lat() : null;
      const lng = place.geometry && place.geometry.location ? place.geometry.location.lng() : null;
      onSelect({
        formatted: place.formatted_address || '',
        postcode: get('postal_code', true),
        locality: get('postal_town') || get('locality'),
        country: get('country', true),
        lat,
        lng,
        raw: place,
      });
    });
    return () => {
      authFailListeners.delete(restoreInput);
      try { google.maps.event.removeListener(listener); } catch (_) { /* noop */ }
    };
  } catch (e) {
    // Autocomplete is a progressive enhancement — silent failure is fine.
    // The plain text input still works for postcodes and free-form addresses.
    restoreInput();
    return () => {};
  }
}
